import type { ApiClient } from "./apiClient";
import { clearTokens, setTokens } from "./apiClient";

/**
 * Auth API service — the subset of packages/frontend/src/services/auth.ts
 * the admin app needs (email/password sign-in, the TOTP MFA challenge and
 * enrolment). Admin accounts are provisioned internally rather than
 * self-registering, so register/verify-email/resend-code/Google sign-in are
 * deliberately not carried over here — see ENGINEERING_DECISIONS.md.
 */

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthUser {
  userId: string;
  email: string;
  name?: string;
}

export interface LoginResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

/**
 * What POST /auth/login returns when the account has authenticator-app MFA
 * enabled, INSTEAD of tokens. Still a 200 — this is the next step, not an
 * error. `session` must be echoed back to `respondMfa` unchanged; it expires
 * a few minutes after this response.
 */
export interface MfaChallenge {
  mfaRequired: true;
  session: string;
  challenge: "SOFTWARE_TOKEN_MFA";
  message: string;
}

export type LoginOutcome = LoginResponse | MfaChallenge;

export function isMfaChallenge(outcome: LoginOutcome): outcome is MfaChallenge {
  return (outcome as MfaChallenge).mfaRequired === true;
}

export interface RespondMfaRequest {
  session: string;
  email: string;
  code: string;
}

export interface MfaStatusResponse {
  /** A software token is registered at all. */
  enrolled: boolean;
  /** The software token is the active MFA method for sign-in. */
  preferred: boolean;
}

export interface EnrolMfaResponse {
  /** Base32 TOTP secret, for manual entry into an authenticator app. */
  secretCode: string;
  /** otpauth://... URI. */
  otpauthUrl: string;
}

export function createAuthApi(api: ApiClient) {
  return {
    /**
     * Login with email + password. Stores tokens on success. When the
     * account has MFA enabled, returns an `MfaChallenge` instead — no tokens
     * exist yet, so nothing is stored; the caller completes sign-in via
     * `respondMfa`.
     */
    login: async (data: LoginRequest): Promise<LoginOutcome> => {
      const response = await api.post<LoginOutcome>("/auth/login", data, {
        noAuth: true,
      });
      if (isMfaChallenge(response)) {
        return response;
      }
      setTokens({
        accessToken: response.accessToken,
        idToken: response.idToken,
        refreshToken: response.refreshToken,
      });
      return response;
    },

    /**
     * Complete sign-in after `login` returned an MfaChallenge, with the
     * 6-digit code from the user's authenticator app.
     */
    respondMfa: async (data: RespondMfaRequest): Promise<LoginResponse> => {
      const response = await api.post<LoginResponse>(
        "/auth/mfa/respond",
        data,
        { noAuth: true }
      );
      setTokens({
        accessToken: response.accessToken,
        idToken: response.idToken,
        refreshToken: response.refreshToken,
      });
      return response;
    },

    /** Whether the signed-in caller has authenticator-app MFA enrolled/preferred. */
    getMfaStatus: () => api.get<MfaStatusResponse>("/auth/mfa"),

    /**
     * Start authenticator-app enrolment. Returns a secret + otpauth:// URI;
     * enrolment is not active until `verifyMfa` succeeds with a code the app
     * produced from this secret.
     */
    enrolMfa: () => api.post<EnrolMfaResponse>("/auth/mfa/enrol"),

    /** Complete enrolment with the first code the authenticator app produced. */
    verifyMfa: (code: string) =>
      api.post<{ verified: true }>("/auth/mfa/verify", { code }),

    /** Logout and clear tokens. The refresh token goes with the request so the server can revoke it. */
    logout: async () => {
      const refreshToken = localStorage.getItem("spark_refresh_token");
      try {
        await api.post("/auth/logout", refreshToken ? { refreshToken } : {});
      } finally {
        clearTokens();
      }
    },
  };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
